import React from "react";

import { Slot } from "~/components/slot";
import { omitBy } from "~/tools/omit-by";

const EVENT_LISTENER_REGEX = /^on[A-Z]/u;

export type EventGateProps<TElement extends React.ElementType> = React.ComponentPropsWithRef<TElement> & {
	listening: boolean;
	children: React.ReactNode;
};

/**
 * The component is a gate that can prevent events from being listened to.
 * It is useful for disabling events on a component when it is in a loading or disabled state
 * for cases it needs to remain focus on the element and user interaction like hover (showing different cursor).
 * For such cases button.disabled cannot work because it will prevent the element from being focused.
 */
export const EventGate = React.forwardRef(({ listening, ...props }, ref: React.ForwardedRef<HTMLElement>) => {
	if (listening) {
		return <Slot ref={ref} {...props} />;
	}

	const propsWithoutEventListeners = omitBy(props, (_, key) => EVENT_LISTENER_REGEX.test(key));

	return <Slot ref={ref} {...propsWithoutEventListeners} />;
}) as {
	<TElement extends React.ElementType = React.ElementType>(
		props: EventGateProps<TElement>,
		ref: React.ForwardedRef<TElement>,
	): React.ReactElement;
	displayName?: string;
};

if (import.meta.env.DEV) {
	EventGate.displayName = "EventGate";
}
